import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ArrowLeft, UserPlus } from "lucide-react";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("student");
  const [coachPassword, setCoachPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await register(email, password, name, role, role === "coach" ? coachPassword : undefined);
      toast.success(`Benvenuto, ${user.name}!`);
      navigate(user.role === "coach" ? "/coach" : "/student");
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(to bottom right, rgba(214, 64, 69, 0.1), rgba(248, 249, 250, 1))`,
      }}
      data-testid="register-page"
    >
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla home
        </Link>

        <Card className="border-0 shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
                <span className="text-white font-heading font-black text-2xl">D</span>
              </div>
              <span className="text-secondary font-heading font-bold text-xl tracking-tight">
                DRAKE TENNIS
              </span>
            </div>
            <CardTitle className="font-heading text-3xl font-bold uppercase tracking-tight">
              Registrati
            </CardTitle>
            <CardDescription>
              Crea il tuo account per iniziare
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium uppercase tracking-wide">
                  Nome
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Mario Rossi"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-12 border-2 focus:border-primary"
                  data-testid="register-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium uppercase tracking-wide">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="mario@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 border-2 focus:border-primary"
                  data-testid="register-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium uppercase tracking-wide">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 border-2 focus:border-primary"
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium uppercase tracking-wide">
                  Tipo Account
                </Label>
                <RadioGroup
                  value={role}
                  onValueChange={setRole}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="student" id="student" data-testid="role-student-radio" />
                    <Label htmlFor="student" className="font-medium cursor-pointer">
                      Allievo
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="coach" id="coach" data-testid="role-coach-radio" />
                    <Label htmlFor="coach" className="font-medium cursor-pointer">
                      Coach
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {role === "coach" && (
                <div className="space-y-2 p-4 bg-primary/5 rounded-sm border-l-4 border-primary">
                  <Label htmlFor="coachPassword" className="text-sm font-medium uppercase tracking-wide">
                    Password Coach
                  </Label>
                  <Input
                    id="coachPassword"
                    type="password"
                    placeholder="Inserisci la password coach"
                    value={coachPassword}
                    onChange={(e) => setCoachPassword(e.target.value)}
                    required
                    className="h-12 border-2 focus:border-primary"
                    data-testid="coach-password-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Richiesta la password speciale per registrarsi come coach
                  </p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  "Registrazione..."
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Registrati
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Hai già un account?{" "}
                <Link 
                  to="/login" 
                  className="text-primary font-semibold hover:underline"
                  data-testid="go-to-login-link"
                >
                  Accedi
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
